import { TableCell } from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import * as customColors from "../../../../theme/customColors";
import {
  buildHaklaiFieldShellStyles,
  buildHaklaiRowFlashStyles,
  buildHaklaiSectionChromeStyles,
  HAKLAI_FIELD_ERROR_SLOT_WIDTH,
  HAKLAI_FIELD_CURRENCY_SLOT_WIDTH,
  HAKLAI_FIELD_SLOT_GAP,
  haklaiAddPillStyles,
  haklaiColumnBorder,
  haklaiLockableSectionStyles,
  haklaiStickyHeaderStyles,
  haklaiTableWrapperBaseStyles
} from "../shared/table/haklaiTableShared";
import { HAKLAI_PROPERTY_PART_LABELS } from "./haklaiPropertyPartsTexts";
import { AMOUNT_INPUT_WIDTH } from "../expenses/haklaiExpensesStyles";

// Same pill as סכום ההוצאה in the expenses table, so a long amount has the
// same room in both sections and the field does not grow with the screen.
export const MONEY_FIELD_WIDTH = AMOUNT_INPUT_WIDTH;
export const PROPERTY_PART_NAME_COLUMN_WIDTH = 156;
export const ADD_ROW_COLUMN_WIDTH = 32;
// The pre-1997 question is one nowrap line: checkbox, gap, and the full
// sentence. The money column is too narrow for that sentence, which is why
// it was wrapping into the shekel mark.
export const PRE1997_ZABAN_COLUMN_WIDTH = 200;
// Longest option is "פטור דירה יחידה": 97px at 700 14px, plus the 24px arrow
// pad, 8px text pad, and the outline. Column width is the content box; the
// 4px cell pad on each side is added by the table, and this column does not
// take leftover table width.
export const PTOR_SELECT_WIDTH = 136;
export const PTOR_COLUMN_WIDTH = PTOR_SELECT_WIDTH;
export const DEPRECIATION_COLUMN_WIDTH = 136;
export const ACTION_COLUMN_WIDTH = 32;
export const VALIDATION_ERROR_COLOR = customColors.red;

// ── Local design tokens ───────────────────────────────────────────────────
// The haklai section needs a handful of off-gray shades that the project
// theme does not yet expose. We keep them co-located here, documented, and
// named by *role* (not hue) so the intent stays visible at every call site
// and so a future theme pass can swap them out in one place.
const palette = {
  // Neutral greys used for disabled / read-only / muted content.
  mutedText: "#8a939d",                   // inactive helper text, placeholders
  subtleText: "#a7afb7",                  // even lighter muted content
  // Soft surfaces for read-only and non-relevant cells.
  readOnlySurface: "#f7f9fb",
  groupBandSurface: "#f0f4f8",            // blue-grey band behind sub-section labels
  optionalFieldSurface: "#fbfcfd",        // trailing "draft" optional row
  // Error palette (focus ring, flash keyframes, text)
  // now lives in `shared/table/haklaiTableShared.js` → `HAKLAI_ERROR_PALETTE`,
  // so the expenses table shares the exact same tokens.
  // Thin rule used for read-only field outlines.
  readOnlyBorder: "rgba(120, 144, 156, 0.28)",
  // Group-frame accents (blue outline that wraps a sub-section body).
  groupFrameBorder: "1px solid rgba(30, 61, 102, 0.14)"
};
export const SALE_VALUE_PLACEHOLDER = HAKLAI_PROPERTY_PART_LABELS.saleValuePlaceholder;
export const BUILT_VALUE_PLACEHOLDER = HAKLAI_PROPERTY_PART_LABELS.builtValuePlaceholder;
const columnGroupFrameBorder = palette.groupFrameBorder;
const TABLE_SCROLL_MEDIA_QUERY = "@media (max-width: 1280px)";
// Dimensions come from the shared tokens so the two tables can never drift.
// Re-exported here for any downstream reader searching by the original name.
const FIELD_SLOT_GAP = HAKLAI_FIELD_SLOT_GAP;
const FIELD_ERROR_SLOT_WIDTH = HAKLAI_FIELD_ERROR_SLOT_WIDTH;
const FIELD_CURRENCY_SLOT_WIDTH = HAKLAI_FIELD_CURRENCY_SLOT_WIDTH;
const MONEY_FIELD_SHELL_WIDTH = MONEY_FIELD_WIDTH + FIELD_ERROR_SLOT_WIDTH + FIELD_CURRENCY_SLOT_WIDTH + (FIELD_SLOT_GAP * 2);
const READ_ONLY_MONEY_FIELD_SHELL_WIDTH = MONEY_FIELD_WIDTH + FIELD_CURRENCY_SLOT_WIDTH + FIELD_SLOT_GAP;
// The value column is the money pill, the error "!", and the shekel mark.
// The same shell is the floor for the זב"ן amount when the pre-1997
// question is not taking the column.
export const STANDARD_VALUE_COLUMN_WIDTH = MONEY_FIELD_SHELL_WIDTH;
const NAME_FIELD_STACK_GAP = 3;
const NAME_FIELD_STACK_MIN_HEIGHT = 34;

// Base cell for the blue group-header row. Every call site sets its own
// background via a class (lightBlue for the title/action cells, a spacer
// class for the empty cells), so no default background here.
//
// RTL note: `textAlign: "start"` + `paddingRight` intentionally mix logical
// and physical properties — the padding override targets MUI's own physical
// `paddingRight` on `.MuiTableCell-sizeSmall:last-child` and must stay in
// sync with the rule it is overriding.
export const StyledGroupCell = withStyles({
  root: {
    padding: "4px 6px",
    "&.MuiTableCell-sizeSmall:last-child": {
      paddingRight: 6
    },
    borderBottom: "none",
    textAlign: "start"
  }
})(TableCell);

export const useHaklaiPropertyPartsStyles = makeStyles((theme) => ({
  // `error={true}` on the TextField adds `.Mui-error` to the outlined input
  // root, which is what the nested selector below hooks into. We override
  // MUI's default 1px/2px (normal/focused) borders with a single 1.5px so
  // the error state stays visually stable across hover & focus without the
  // input "jumping" to a 2px focused border.
  numberInput: {
    width: MONEY_FIELD_WIDTH,
    "& .MuiOutlinedInput-root.Mui-error": {
      "& fieldset": {
        borderColor: VALIDATION_ERROR_COLOR,
        borderWidth: 1.5
      },
      "&:hover fieldset": {
        borderColor: VALIDATION_ERROR_COLOR
      },
      "&.Mui-focused fieldset": {
        borderColor: VALIDATION_ERROR_COLOR,
        borderWidth: 1.5
      }
    },
    "& input": {
      // Match the expenses amount pill: the value sits in the middle.
      textAlign: "center",
      padding: "6px 8px",
      fontSize: "0.85rem"
    },
    "& .MuiFormHelperText-root.Mui-error, & .MuiSvgIcon-root.Mui-error": {
      color: VALIDATION_ERROR_COLOR
    }
  },
  readOnlyField: {
    width: MONEY_FIELD_WIDTH,
    "& .MuiOutlinedInput-root": {
      backgroundColor: palette.readOnlySurface,
      color: customColors.darkGray,
      "& fieldset": {
        borderColor: palette.readOnlyBorder,
        borderWidth: 1
      },
      "&:hover fieldset": {
        borderColor: palette.readOnlyBorder
      },
      "&.Mui-focused fieldset": {
        borderColor: palette.readOnlyBorder,
        borderWidth: 1
      }
    },
    "& .MuiOutlinedInput-input": {
      textAlign: "center",
      padding: "6px 8px",
      fontSize: "0.85rem",
      color: customColors.darkGray,
      cursor: "default"
    }
  },
  readOnlyFieldWide: {
    width: MONEY_FIELD_WIDTH,
    "& .MuiOutlinedInput-root": {
      backgroundColor: palette.readOnlySurface,
      color: customColors.darkGray,
      "& fieldset": {
        borderColor: palette.readOnlyBorder,
        borderWidth: 1
      },
      "&:hover fieldset": {
        borderColor: palette.readOnlyBorder
      },
      "&.Mui-focused fieldset": {
        borderColor: palette.readOnlyBorder,
        borderWidth: 1
      }
    },
    "& .MuiOutlinedInput-input": {
      textAlign: "center",
      padding: "5px 6px",
      fontSize: "0.85rem",
      color: customColors.darkGray,
      cursor: "default"
    }
  },
  ...haklaiLockableSectionStyles,
  nonRelevantCell: {
    backgroundColor: palette.readOnlySurface
  },
  nonRelevantValue: {
    color: palette.subtleText,
    fontSize: "0.86rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: 1.25,
    textAlign: "center"
  },
  zabanDetailsStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    margin: "0 auto",
    width: "100%"
  },
  pre1997QuestionWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    color: customColors.darkGray
  },
  pre1997QuestionLabel: {
    fontSize: "0.71rem",
    fontWeight: 600,
    lineHeight: 1.15,
    textAlign: "center",
    whiteSpace: "nowrap"
  },
  pre1997Checkbox: {
    padding: 2
  },
  ...buildHaklaiFieldShellStyles(),
  moneyFieldShell: {
    width: MONEY_FIELD_SHELL_WIDTH,
    maxWidth: "100%"
  },
  readOnlyMoneyFieldShell: {
    width: READ_ONLY_MONEY_FIELD_SHELL_WIDTH,
    maxWidth: "100%",
    "& $fieldShellStatusSlot": {
      display: "none"
    }
  },
  ptorFieldShell: {
    width: PTOR_SELECT_WIDTH,
    maxWidth: "100%",
    "& $fieldShellInputSlot": {
      flex: "0 0 auto",
      width: PTOR_SELECT_WIDTH,
      maxWidth: "100%"
    },
    "& $fieldShellStatusSlot": {
      display: "none"
    }
  },
  ptorCellStack: {
    display: "flex",
    position: "relative",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
    minHeight: 34,
    width: "100%"
  },
  ptorSelect: {
    width: PTOR_SELECT_WIDTH,
    maxWidth: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 20
    },
    "& .MuiOutlinedInput-input": {
      padding: "5px 8px 5px 24px",
      fontSize: "0.875rem",
      fontWeight: 700,
      color: customColors.darkGray,
      textAlign: "right"
    },
    "& .MuiSelect-select": {
      fontSize: "0.875rem",
      textAlign: "right"
    },
    "& .MuiSelect-icon": {
      left: 4,
      right: "auto"
    }
  },
  tzinunQuestionDialog: {
    direction: "rtl",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(2),
    minWidth: 280
  },
  tzinunQuestionDialogText: {
    color: customColors.darkGray,
    fontWeight: 700,
    lineHeight: 1.5
  },
  tzinunCheckerPrompt: {
    color: customColors.darkGray,
    lineHeight: 1.6
  },
  tzinunCheckerLink: {
    display: "inline-flex",
    verticalAlign: "baseline",
    color: customColors.lightBlue,
    fontWeight: 700,
    textDecoration: "underline",
    textUnderlineOffset: 2,
    padding: 0,
    minWidth: 0,
    "&:hover": {
      color: customColors.darkBlue
    }
  },
  tzinunQuestionDialogActions: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  // Same containment as סוג ניכוי: the pill fills the cell, and the
  // padding stays inside that width instead of pushing into the next column.
  nameFieldShell: {
    width: "100%",
    maxWidth: "100%",
    "& $fieldShellStatusSlot": {
      display: "none"
    },
    "& $fieldShellInputSlot": {
      width: "100%",
      maxWidth: "100%",
      minWidth: 0
    }
  },
  depreciationFieldContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: 34
  },
  rentalDepreciationFieldShell: {
    width: "100%",
    maxWidth: "100%",
    position: "relative",
    justifyContent: "center",
    gap: 0,
    margin: 0,
    // Keep the error "!" out of flex flow so כן/לא stay visually centered.
    "& $fieldShellStatusSlot": {
      position: "absolute",
      insetInlineStart: 0,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 1
    }
  },
  rentalDepreciationControl: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
    direction: "rtl",
    minHeight: 34,
    width: "100%",
    whiteSpace: "nowrap"
  },
  rentalDepreciationActionRow: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    margin: 0,
    direction: "rtl"
  },
  rentalDepreciationRadioGroup: {
    display: "inline-flex !important",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: 4,
    margin: 0
  },
  rentalDepreciationRadioOption: {
    margin: 0,
    minWidth: 0
  },
  rentalDepreciationRadio: {
    padding: 0
  },
  rentalDepreciationEditButton: {
    width: 26,
    height: 26,
    padding: 0,
    borderRadius: 6,
    color: "#1e3d66",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid transparent",
    transition: theme.transitions.create(["background-color", "border-color", "color"], {
      duration: theme.transitions.duration.shortest
    }),
    "& .MuiIconButton-label": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 0
    },
    "& .MuiSvgIcon-root": {
      display: "block",
      fontSize: 16
    },
    "&:hover": {
      backgroundColor: "#edf6fa",
      borderColor: "rgba(51, 107, 136, 0.32)"
    }
  },
  rentalDepreciationEditButtonActive: {
    backgroundColor: "#f7fbfd",
    borderColor: "rgba(51, 107, 136, 0.28)"
  },
  rentalDepreciationEditButtonWarning: {
    borderColor: VALIDATION_ERROR_COLOR,
    "&:hover": {
      borderColor: VALIDATION_ERROR_COLOR
    }
  },
  rentalDepreciationEditButtonDisabled: {
    color: "#a7b0b8",
    cursor: "default",
    opacity: 0.55,
    borderColor: "transparent",
    backgroundColor: "transparent",
    "&:hover": {
      color: "#a7b0b8",
      borderColor: "transparent",
      backgroundColor: "transparent"
    }
  },
  fieldShellPlaceholder: {
    width: MONEY_FIELD_WIDTH,
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  headerLabelText: {
    color: "#1e3d66",
    fontFamily: "inherit",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "inherit",
    lineHeight: 1.3,
    whiteSpace: "normal",
    direction: "rtl",
    unicodeBidi: "isolate"
  },
  addColumnDividerCell: {
    borderInlineEnd: "none !important"
  },
  columnGroupCell: {
    position: "relative",
    borderInlineStart: "none !important",
    borderInlineEnd: "none !important",
    boxShadow: "none !important",
    "--column-group-bottom": "0 solid transparent",
    "--column-group-start": "0 solid transparent",
    "--column-group-end": "0 solid transparent",
    "--column-group-inner-start": "0 solid transparent",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      borderBottom: "var(--column-group-bottom)",
      borderInlineStart: "var(--column-group-start)",
      borderInlineEnd: "var(--column-group-end)"
    },
    "&::before": {
      content: '""',
      position: "absolute",
      insetBlock: 0,
      insetInlineStart: 0,
      pointerEvents: "none",
      borderInlineStart: "var(--column-group-inner-start)"
    }
  },
  columnGroupBottom: {
    "--column-group-bottom": columnGroupFrameBorder
  },
  columnGroupStart: {
    "--column-group-start": columnGroupFrameBorder
  },
  columnGroupInnerStart: {
    "--column-group-inner-start": haklaiColumnBorder
  },
  columnGroupEnd: {
    "--column-group-end": columnGroupFrameBorder
  },
  groupFrameTrailingActionCell: {
    borderInlineStart: "none !important"
  },
  tableWrapper: {
    ...haklaiTableWrapperBaseStyles,
    position: "relative",
    zIndex: 0,
    display: "block",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    marginTop: 0,
    border: 0,
    borderRadius: 0,
    backgroundColor: "transparent",
    boxShadow: "none",
    [TABLE_SCROLL_MEDIA_QUERY]: {
      display: "block",
      width: "100%",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch"
    },
    [theme.breakpoints.down("xs")]: {
      "--haklai-nav-height": "56px"
    }
  },
  partsTable: {
    backgroundColor: "transparent",
    borderCollapse: "separate",
    borderSpacing: 0
  },
  tableWrapperScrollable: {
    display: "block",
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch"
  },
  tableFrameAlign: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%"
  },
  stickyHeader: {
    "& .MuiTableCell-head": {
      ...haklaiStickyHeaderStyles,
      backgroundColor: "#d9e7f2",
      zIndex: 2
    },
    [TABLE_SCROLL_MEDIA_QUERY]: {
      "& .MuiTableCell-head": {
        position: "static"
      }
    }
  },
  stickyHeaderDisabled: {
    "& .MuiTableCell-head": {
      position: "static"
    }
  },
  subSectionCell: {
    backgroundColor: "transparent",
    textAlign: "center",
    verticalAlign: "middle",
    fontWeight: 700,
    color: "#1e3d66",
    padding: "5px 4px"
  },
  subSectionCellInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    minHeight: 28
  },
  subSectionCellText: {
    fontWeight: 700,
    fontSize: "0.82rem"
  },
  groupTitleCell: {
    backgroundColor: "#fff6e4 !important",
    borderBottom: "0 !important",
    padding: "14px 16px !important",
    textAlign: "right"
  },
  groupTitleText: {
    color: "#1e3d66",
    fontSize: "15px",
    fontWeight: 800,
    lineHeight: 1.3,
    textAlign: "right"
  },
  emptyStateCell: {
    backgroundColor: "transparent",
    padding: "12px 10px",
    textAlign: "center"
  },
  emptyStateText: {
    color: palette.mutedText,
    fontSize: "0.84rem",
    fontWeight: 600,
    lineHeight: 1.35
  },
  rowActionCellContent: {
    width: "100%",
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    flexWrap: "nowrap"
  },
  rowAddCell: {
    width: ADD_ROW_COLUMN_WIDTH,
    minWidth: ADD_ROW_COLUMN_WIDTH,
    maxWidth: ADD_ROW_COLUMN_WIDTH,
    padding: "2px !important"
  },
  rowAddCellContent: {
    width: "100%",
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  ...haklaiAddPillStyles,
  // Applied to the name / sale-value / built-value inputs while the row is
  // the trailing row and no user-facing field has data yet (see
  // `isVisuallyDrafting` in `getPropertyPartRowValidationState`). The
  // transition softens the pale-to-normal handoff when the user commits
  // the first character of real data, avoiding a hard "flash" even though
  // the flip is scoped to the input they're typing in.
  optionalTrailingField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: palette.optionalFieldSurface,
      transition: "background-color 180ms ease"
    },
    "& .MuiOutlinedInput-input": {
      color: palette.mutedText,
      transition: "color 180ms ease"
    }
  },
  readOnlyNameInput: {
    width: "100%",
    maxWidth: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 20,
      boxSizing: "border-box",
      width: "100%"
    },
    "& .MuiOutlinedInput-input": {
      boxSizing: "border-box",
      width: "100%",
      fontSize: "0.85rem",
      fontWeight: 400,
      paddingTop: 5,
      paddingBottom: 5,
      paddingInlineStart: 12,
      paddingInlineEnd: 12,
      textAlign: "center",
      cursor: "default",
      color: "#787878",
      WebkitTextFillColor: "#787878",
      minWidth: 0,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden"
    }
  },
  readOnlyNameInputError: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: `${VALIDATION_ERROR_COLOR} !important`
    }
  },
  nameFieldStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: NAME_FIELD_STACK_GAP,
    margin: "0 auto",
    minHeight: NAME_FIELD_STACK_MIN_HEIGHT,
    width: "100%",
    maxWidth: "100%"
  },
  nameFieldTooltipAnchor: {
    display: "block",
    width: "100%",
    maxWidth: "100%"
  },
  nameFieldStackCentered: {
    justifyContent: "center"
  },
  ...buildHaklaiSectionChromeStyles(theme, {
    sectionContent: {
      padding: "16px 12px"
    }
  }),

  // Row flash styles live in the shared factory so the expenses sheet spreads
  // the exact same keys. The field error indicator
  // (`fieldErrorIcon`, `fieldErrorIconHidden`, `visuallyHidden`) already
  // comes from `buildHaklaiFieldShellStyles()` above.
  ...buildHaklaiRowFlashStyles()
}));
